import { fxConfig } from './config';
import { AUDIO_ASSETS, type AudioGroup, type SfxKey } from './audio.gen';
import { busVolume, ctx, currentCtx, loadBuffer, readyBuffer, setBusVolume, sfxOut } from './audioCore';

/**
 * Sound effects: recorded buffers (design/audio-raw → public/audio, see scripts/audio/web.mjs)
 * with the old Web Audio synthesis as a fallback while a buffer is not loaded (or failed).
 * Silent unless the player enabled sound in the profile (fxConfig.sound); nothing is fetched before that.
 *
 *   sound.play('win');            // legacy name → common/win
 *   sound.play('carp/gate');      // any recorded key `group/key`
 *   sound.preload('carp');        // on entering a game screen (done by useMusicRoute)
 *   sound.loopStart('roulette/strip-loop'); sound.loopSet('roulette/strip-loop', { rate: 1.2 }); sound.loopStop(…);
 */
export type LegacySoundName =
  | 'tick'
  | 'click'
  | 'stop'
  | 'win'
  | 'big'
  | 'coin'
  | 'pop'
  | 'thud'
  | 'flip'
  | 'launch'
  | 'boom'
  | 'level'
  | 'crash';

export type SoundName = LegacySoundName | SfxKey;

/** Legacy names → recorded keys. */
export const LEGACY_SOUNDS: Record<LegacySoundName, SfxKey> = {
  tick: 'common/tick',
  click: 'common/click',
  stop: 'common/stop',
  win: 'common/win',
  big: 'common/big',
  coin: 'common/coin',
  pop: 'common/pop',
  thud: 'common/thud',
  flip: 'common/flip',
  launch: 'crash/launch',
  boom: 'common/boom',
  level: 'common/level',
  crash: 'crash/crash',
};

export function resolveSound(name: SoundName): SfxKey {
  return (LEGACY_SOUNDS as Record<string, SfxKey>)[name] ?? (name as SfxKey);
}

// ---- synthesis (fallback) --------------------------------------------------------------------------------

function tone(f: number, d: number, type: OscillatorType = 'sine', v = 0.1, t0 = 0, f2?: number): void {
  const c = ctx();
  if (!c) return;
  const t = c.currentTime + t0;
  const g = c.createGain();
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(f, t);
  if (f2) o.frequency.exponentialRampToValueAtTime(f2, t + d);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(v, t + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t + d);
  o.connect(g).connect(sfxOut(c));
  o.start(t);
  o.stop(t + d + 0.05);
}

function noiseCrash(): void {
  const c = ctx();
  if (!c) return;
  const n = Math.floor(c.sampleRate * 0.7);
  const b = c.createBuffer(1, n, c.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, 2.5);
  const s = c.createBufferSource();
  const f = c.createBiquadFilter();
  const g = c.createGain();
  f.type = 'lowpass';
  f.frequency.value = 900;
  g.gain.value = 0.35;
  s.buffer = b;
  s.connect(f).connect(g).connect(sfxOut(c));
  s.start();
  tone(220, 0.5, 'sawtooth', 0.06, 0, 55);
}

const SYNTH: Record<LegacySoundName, () => void> = {
  tick: () => tone(2200, 0.025, 'square', 0.025),
  click: () => tone(760, 0.04, 'square', 0.04),
  stop: () => {
    tone(170, 0.14, 'sine', 0.3, 0, 60);
    tone(1200, 0.03, 'square', 0.025);
  },
  win: () => [523, 659, 784, 1047].forEach((f, i) => tone(f, 0.28, 'triangle', 0.12, i * 0.07)),
  big: () =>
    [392, 523, 659, 784, 1047, 1319, 1568].forEach((f, i) => tone(f, 0.5, 'triangle', 0.12, i * 0.09)),
  coin: () => tone(1500 + Math.random() * 700, 0.09, 'sine', 0.06, 0, 2600),
  pop: () => tone(420 + Math.random() * 380, 0.1, 'sine', 0.14, 0, 1300),
  thud: () => tone(110, 0.18, 'sine', 0.3, 0, 45),
  flip: () => tone(300, 0.12, 'triangle', 0.08, 0, 900),
  launch: () => tone(300, 0.45, 'sine', 0.03, 0, 1400),
  boom: () => {
    tone(90, 0.5, 'sine', 0.22, 0, 35);
    tone(2400 + Math.random() * 800, 0.5, 'triangle', 0.02, 0.05, 900);
  },
  level: () => [523, 784, 1047, 1568].forEach((f, i) => tone(f, 0.6, 'square', 0.05, i * 0.11)),
  crash: noiseCrash,
};

/** Recorded key → synthesis fallback (the legacy sounds, plus the coin stream built from coin plinks). */
const SYNTH_BY_KEY = new Map<SfxKey, () => void>([
  ...(Object.keys(LEGACY_SOUNDS) as LegacySoundName[]).map((n): [SfxKey, () => void] => [
    LEGACY_SOUNDS[n],
    SYNTH[n],
  ]),
  [
    'common/coins',
    () =>
      [0, 0.09, 0.2, 0.33, 0.48].forEach((t) =>
        tone(1500 + Math.random() * 700, 0.09, 'sine', 0.05, t, 2600),
      ),
  ],
]);

// ---- loading ---------------------------------------------------------------------------------------------

const SFX_KEYS = Object.keys(AUDIO_ASSETS).filter((k) => !k.startsWith('music/')) as SfxKey[];
/**
 * Always loaded: `common`, and `tourney` — «your table is ready» / «next stage» arrive on any screen
 * (two short files).
 */
const ALWAYS: readonly AudioGroup[] = ['common', 'tourney'];
const wantedGroups = new Set<AudioGroup>(ALWAYS);

function loadGroup(group: AudioGroup): void {
  for (const k of SFX_KEYS) if (k.startsWith(`${group}/`)) void loadBuffer(k);
}

// ---- loops -----------------------------------------------------------------------------------------------

export interface PlayOptions {
  /** 0…1 */
  volume?: number;
  /** playbackRate, clamped to 0.5…2.5 */
  rate?: number;
  /** a skipped celebration (useCelebration's signal) fades the recording out instead of letting it ring on */
  signal?: AbortSignal;
}

export interface LoopOptions {
  /** playbackRate (pitch + speed), clamped to 0.5…2.5 */
  rate?: number;
  /** 0…1 */
  volume?: number;
}

interface Loop {
  src: AudioBufferSourceNode;
  gain: GainNode;
}
const loops = new Map<SfxKey, Loop>();

/** One-shots long enough to be a celebration (win, big, gate, hu …): a skipped celebration fades them all out. */
const LONG_SHOT_S = 0.6;
const longShots = new Set<Loop>();

function fadeOut(l: Loop, c: AudioContext): void {
  try {
    l.gain.gain.setTargetAtTime(0.0001, c.currentTime, 0.04);
    l.src.stop(c.currentTime + 0.2);
  } catch {
    /* already stopped */
  }
}
const clampRate = (r: number) => Math.min(2.5, Math.max(0.5, r));

function stopLoop(key: SfxKey, fade = 0.12): void {
  const l = loops.get(key);
  const c = currentCtx();
  loops.delete(key);
  if (!l || !c) return;
  try {
    l.gain.gain.setTargetAtTime(0.0001, c.currentTime, fade / 3);
    l.src.stop(c.currentTime + fade + 0.05);
  } catch {
    /* ignore */
  }
}

// ---- Liftoff hum -----------------------------------------------------------------------------------------

const ENGINE: SfxKey = 'crash/engine-loop';

/** playbackRate of the engine loop for a multiplier: ×1 → 0.9, ×2 → 1.2, ×4 → 1.5 … capped at 2. */
export function engineRate(multiplier: number): number {
  const m = Number.isFinite(multiplier) && multiplier > 1 ? multiplier : 1;
  return Math.min(2, Math.max(0.9, 0.9 + 0.3 * Math.log2(m)));
}

interface Hum {
  osc: OscillatorNode;
  gain: GainNode;
}
/** oscillator fallback when the recorded engine loop is not ready */
let hum: Hum | null = null;

// ---- API -------------------------------------------------------------------------------------------------

export const sound = {
  get enabled(): boolean {
    return fxConfig.sound;
  },
  /** Plays a recorded buffer if loaded; otherwise the synthesized fallback (silence for keys without one). */
  play(name: SoundName, opts: PlayOptions = {}): void {
    if (!fxConfig.sound || opts.signal?.aborted) return;
    try {
      const key = resolveSound(name);
      const buf = readyBuffer(key);
      if (buf) {
        const c = ctx();
        if (!c) return;
        const src = c.createBufferSource();
        src.buffer = buf;
        if (opts.rate) src.playbackRate.value = clampRate(opts.rate);
        const g = c.createGain();
        g.gain.value = Math.min(1, Math.max(0, opts.volume ?? 1));
        src.connect(g).connect(sfxOut(c));
        src.start();
        const shot: Loop = { src, gain: g };
        if (buf.duration > LONG_SHOT_S) {
          longShots.add(shot);
          src.onended = () => longShots.delete(shot);
        }
        opts.signal?.addEventListener('abort', () => fadeOut(shot, c), { once: true });
        return;
      }
      if (key in AUDIO_ASSETS) void loadBuffer(key);
      SYNTH_BY_KEY.get(key)?.();
    } catch {
      /* audio may be blocked — never break the game */
    }
  },
  /** Honest-play rule 3: a skipped celebration (useCelebration.skip) fades out every long one-shot still ringing. */
  cutCelebration(): void {
    const c = currentCtx();
    if (c) for (const l of longShots) fadeOut(l, c);
    longShots.clear();
  },
  /** Load a group's buffers (`common` always). Only fetches while sound is on; remembered for later otherwise. */
  preload(...groups: AudioGroup[]): void {
    for (const g of groups) if (g !== 'music') wantedGroups.add(g);
    if (!fxConfig.sound) return;
    for (const g of groups) if (g !== 'music') loadGroup(g);
  },
  /** Called by useFxSync after the settings change: load what was asked for, or stop loops when switched off. */
  sync(): void {
    if (fxConfig.sound) {
      for (const g of wantedGroups) loadGroup(g);
    } else {
      sound.stopAll();
    }
  },
  /** Starts a seamless loop. Returns false (and starts loading) if the buffer is not ready or sound is off. */
  loopStart(key: SfxKey, opts: LoopOptions = {}): boolean {
    if (!fxConfig.sound) return false;
    if (loops.has(key)) {
      sound.loopSet(key, opts);
      return true;
    }
    const buf = readyBuffer(key);
    if (!buf) {
      void loadBuffer(key);
      return false;
    }
    const c = ctx();
    if (!c) return false;
    try {
      const src = c.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      // encoder padding must not become a gap: loop exactly the source length
      const len = AUDIO_ASSETS[key].durationMs / 1000;
      if (len > 0 && len < buf.duration) {
        src.loopStart = 0;
        src.loopEnd = len;
      }
      src.playbackRate.value = clampRate(opts.rate ?? 1);
      const gain = c.createGain();
      gain.gain.value = 0.0001;
      gain.gain.setTargetAtTime(Math.min(1, Math.max(0, opts.volume ?? 1)), c.currentTime, 0.08);
      src.connect(gain).connect(sfxOut(c));
      src.start();
      loops.set(key, { src, gain });
      return true;
    } catch {
      return false;
    }
  },
  /** Smoothly changes rate / volume of a running loop. */
  loopSet(key: SfxKey, opts: LoopOptions): void {
    const l = loops.get(key);
    const c = currentCtx();
    if (!l || !c) return;
    if (opts.rate !== undefined)
      l.src.playbackRate.setTargetAtTime(clampRate(opts.rate), c.currentTime, 0.12);
    if (opts.volume !== undefined)
      l.gain.gain.setTargetAtTime(Math.min(1, Math.max(0.0001, opts.volume)), c.currentTime, 0.08);
  },
  loopStop(key: SfxKey): void {
    stopLoop(key);
  },
  isLooping(key: SfxKey): boolean {
    return loops.has(key);
  },
  /** Stop every loop and the hum (sound switched off, page left). */
  stopAll(): void {
    for (const k of [...loops.keys()]) stopLoop(k);
    sound.humStop();
  },
  /** Liftoff engine: the recorded loop with playbackRate from the multiplier, oscillator hum as the fallback. */
  humStart(): void {
    if (!fxConfig.sound || hum || loops.has(ENGINE)) return;
    if (sound.loopStart(ENGINE, { rate: engineRate(1) })) return;
    const c = ctx();
    if (!c) return;
    try {
      const osc = c.createOscillator();
      const gain = c.createGain();
      const f = c.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = 700;
      osc.type = 'sawtooth';
      osc.frequency.value = 90;
      gain.gain.value = 0.0001;
      gain.gain.exponentialRampToValueAtTime(0.05, c.currentTime + 0.3);
      osc.connect(f).connect(gain).connect(sfxOut(c));
      osc.start();
      hum = { osc, gain };
    } catch {
      hum = null;
    }
  },
  humSet(multiplier: number): void {
    if (loops.has(ENGINE)) {
      sound.loopSet(ENGINE, { rate: engineRate(multiplier) });
      return;
    }
    const c = currentCtx();
    if (!hum || !c) return;
    hum.osc.frequency.setTargetAtTime(80 + multiplier * 45, c.currentTime, 0.05);
  },
  humStop(): void {
    stopLoop(ENGINE, 0.15);
    const c = currentCtx();
    if (!hum || !c) {
      hum = null;
      return;
    }
    try {
      hum.gain.gain.setTargetAtTime(0.0001, c.currentTime, 0.05);
      hum.osc.stop(c.currentTime + 0.3);
    } catch {
      /* ignore */
    }
    hum = null;
  },
  /** SFX master volume 0…1. */
  get volume(): number {
    return busVolume('sfx');
  },
  setVolume(v: number): void {
    setBusVolume('sfx', v);
  },
  get musicEnabled(): boolean {
    return fxConfig.music;
  },
};

/** Tests only. */
export function __resetSound(): void {
  loops.clear();
  hum = null;
  wantedGroups.clear();
  for (const g of ALWAYS) wantedGroups.add(g);
}
